import { m } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { fadeIn, fadeInScale, slideInUp } from '@/lib/motion-variants';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Whether to show as inline (no margins) */
  inline?: boolean;
}

const spinnerSizeClasses = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

export function LoadingSpinner({ size = 'md', className = '', inline = false }: LoadingSpinnerProps) {
  return (
    <m.div
      {...fadeIn}
      className={cn(
        'animate-spin rounded-full border-2 border-primary/20 border-t-primary transition-all duration-300',
        spinnerSizeClasses[size],
        inline ? '' : 'mx-auto',
        className,
      )}
    />
  );
}

interface LoadingStateProps {
  /** Loading message */
  message?: string;
  /** Icon to display instead of spinner */
  icon?: LucideIcon;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Whether to show default loading message if none provided */
  showDefaultMessage?: boolean;
}

const containerPadding = {
  sm: 'py-4',
  md: 'py-12',
  lg: 'py-16',
};

const iconSizeClasses = {
  sm: 'h-6 w-6',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
};

export function LoadingState({
  message,
  icon: Icon,
  size = 'md',
  className = '',
  showDefaultMessage = true,
}: LoadingStateProps) {
  const { t } = useTranslation();

  const displayMessage = message || (showDefaultMessage ? t('general.loading') : undefined);

  return (
    <div className={cn('flex items-center justify-center', containerPadding[size], className)}>
      <m.div {...fadeInScale} className='text-center'>
        {Icon ? (
          <Icon className={cn('mx-auto mb-4 text-primary animate-pulse', iconSizeClasses[size])} />
        ) : (
          <LoadingSpinner size={size} className='mb-4' />
        )}
        {displayMessage && (
          <m.p
            {...slideInUp}
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1], delay: 0.1 }}
            className='mt-4 text-muted-strong'
          >
            {displayMessage}
          </m.p>
        )}
      </m.div>
    </div>
  );
}

interface PageLoadingStateProps {
  /** Loading message */
  message?: string;
  /** Icon to display instead of spinner */
  icon?: LucideIcon;
}

export function PageLoadingState({ message, icon }: PageLoadingStateProps) {
  return (
    <div className='min-h-screen bg-light-50 dark:bg-gray-900 flex items-center justify-center'>
      <LoadingState message={message} icon={icon} size='lg' />
    </div>
  );
}

interface InlineLoadingStateProps {
  /** Loading message */
  message?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Whether to show default loading message if none provided */
  showDefaultMessage?: boolean;
}

export function InlineLoadingState({ message, size = 'sm', showDefaultMessage = true }: InlineLoadingStateProps) {
  const { t } = useTranslation();

  const displayMessage = message || (showDefaultMessage ? t('general.loading') : undefined);

  return (
    <div className='flex items-center space-x-2'>
      <LoadingSpinner size={size === 'sm' ? 'sm' : 'md'} inline />
      {displayMessage && <span className='text-muted-strong text-sm'>{displayMessage}</span>}
    </div>
  );
}
